/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useState } from 'react';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';
import { useWidgetHeightRefresh } from '../../Hooks/useWidgetHeightRefresh/useWidgetHeightRefresh';
import useClientApiCopy from './copy/useClientApiCopy';
import makeRoofChangeQuestionStyles from './makeRoofChangeQuestionStyles';

const HEIGHT_REFRESH_DELAY_MS = 250;

const FOLLOW_UP_VALUES = [
  'HOUSE_EXTENSION',
  'ROOF_REPAIR',
  'LOFT_CONVERSION',
  'OTHER',
];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;
const formatImageryDate = (value, localeCode) => {
  if (!ISO_DATE.test(value ?? '')) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString((localeCode ?? 'en-GB').replace('_', '-'), {
    month: 'long',
    year: 'numeric',
  });
};

const WarningIcon = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    role="img"
    aria-label="Warning"
  >
    <path
      d="M12 9V13M12 17H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0377 2.66667 10.2679 4L3.33975 16C2.56995 17.3333 3.53223 19 5.07183 19Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// The committed answer is `value` ('NONE' | a change type | undefined), owned by
// the parent/store. Only the transient UI lives here: `isEditing` reopens an
// answered question, and `isYesExpanded` holds the moment after Yes before a
// type is picked (no committed value exists yet).
const RoofChangeQuestion = ({
  solarPotentialAge,
  localeCode,
  value,
  onChange,
}) => {
  const theme = useTheme();
  const classes = makeRoofChangeQuestionStyles({ theme });
  const copy = useClientApiCopy();
  const [isEditing, setIsEditing] = useState(false);
  const [isYesExpanded, setIsYesExpanded] = useState(false);

  const isAnswered = value !== undefined;
  const selectedOption = FOLLOW_UP_VALUES.includes(value) ? value : null;

  useWidgetHeightRefresh(
    [value, isEditing, isYesExpanded],
    HEIGHT_REFRESH_DELAY_MS
  );

  const showSummary = isAnswered && !isEditing;
  const showFollowUp = isYesExpanded && !showSummary;

  const handleNo = () => {
    setIsYesExpanded(false);
    setIsEditing(false);
    onChange('NONE');
  };

  const handleYes = () => {
    setIsYesExpanded(true);
  };

  const handleOption = (option) => {
    setIsYesExpanded(false);
    setIsEditing(false);
    onChange(option);
  };

  const handleEdit = () => {
    setIsYesExpanded(false);
    setIsEditing(true);
    onChange(undefined);
  };

  if (showSummary) {
    const summaryValue =
      value === 'NONE'
        ? copy.solar.roofChangeSummaryNone
        : copy.solar.options[value] ?? copy.solar.roofChangeSummaryNone;

    return (
      <Box css={classes.summary} data-testid="roofChangeSummary">
        <Box css={classes.summaryText}>
          <Box css={classes.summaryLabel}>
            {copy.solar.roofChangeSummaryLabel}
          </Box>
          <Box css={classes.summaryValue} data-testid="roofChangeSummaryValue">
            {summaryValue}
          </Box>
        </Box>
        <button
          type="button"
          data-testid="roofChangeEdit"
          aria-label={copy.solar.edit}
          css={classes.editButton}
          onClick={handleEdit}
        >
          <EditIcon />
        </button>
      </Box>
    );
  }

  return (
    <Box>
      <Box css={classes.banner}>
        <Box css={classes.warningIcon} aria-hidden>
          <WarningIcon />
        </Box>
        <Box css={classes.message}>
          {copy.solar.roofChangePromptPrefix}
          <strong>{formatImageryDate(solarPotentialAge, localeCode)}</strong>
          {copy.solar.roofChangePromptSuffix}
        </Box>
        <Box css={classes.buttons}>
          <button
            type="button"
            data-testid="roofChangeYes"
            css={classes.yesButton}
            onClick={handleYes}
          >
            {copy.solar.roofChangeYes}
          </button>
          <button
            type="button"
            data-testid="roofChangeNo"
            css={classes.noButton}
            onClick={handleNo}
          >
            {copy.solar.roofChangeNo}
          </button>
        </Box>
      </Box>
      {showFollowUp && (
        <Box css={classes.followUp} data-testid="roofChangeFollowUp">
          <Box css={classes.followUpQuestion}>
            {copy.solar.roofChangeFollowUp}
          </Box>
          <Box css={classes.options}>
            {FOLLOW_UP_VALUES.map((option) => (
              <button
                type="button"
                key={option}
                data-testid={`roofChangeOption-${option}`}
                css={[
                  classes.option,
                  selectedOption === option && classes.optionSelected,
                ]}
                onClick={() => handleOption(option)}
              >
                {copy.solar.options[option]}
              </button>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RoofChangeQuestion;
